/** Small on-demand stills for the paired controller, never a second media stream. */
export const TV_STAGE_PREVIEW = 'tv:stage-preview';
export const TV_STAGE_PREVIEW_REQUEST = 'tv:stage-preview:request';
export const TV_STAGE_PREVIEW_WIDTH = 480;
export const TV_STAGE_PREVIEW_HEIGHT = 270;
export const TV_STAGE_PREVIEW_MAX_BYTES = 64 * 1024;
export const TV_STAGE_PREVIEW_INTERVAL_MS = 2000;

export interface TvStagePreviewFrame { image: string; width: number; height: number }
export type TvStagePreviewReply =
  | { ok: true; frame: TvStagePreviewFrame }
  | { ok: false; reason: 'unavailable' | 'unsupported' | 'busy' };

/** Checks both transport size and JPEG dimensions before a receiver-supplied image is relayed. */
export function isTvStagePreviewFrame(value: unknown): value is TvStagePreviewFrame {
  if (!value || typeof value !== 'object') return false;
  const frame = value as TvStagePreviewFrame;
  const prefix = 'data:image/jpeg;base64,';
  if (frame.width !== TV_STAGE_PREVIEW_WIDTH || frame.height !== TV_STAGE_PREVIEW_HEIGHT
    || typeof frame.image !== 'string' || frame.image.length > prefix.length + Math.ceil(TV_STAGE_PREVIEW_MAX_BYTES / 3) * 4
    || !frame.image.startsWith(prefix)) return false;
  try {
    const bytes = atob(frame.image.slice(prefix.length));
    const byte = (offset: number) => bytes.charCodeAt(offset);
    if (bytes.length > TV_STAGE_PREVIEW_MAX_BYTES || byte(0) !== 255 || byte(1) !== 216) return false;
    let offset = 2;
    while (offset + 4 <= bytes.length) {
      if (byte(offset++) !== 255) return false;
      const marker = byte(offset++);
      const length = (byte(offset) << 8) | byte(offset + 1);
      if (length < 2 || offset + length > bytes.length || marker === 218) return false;
      if (marker === 192 || marker === 193 || marker === 194) {
        return length >= 8 && ((byte(offset + 3) << 8) | byte(offset + 4)) === frame.height
          && ((byte(offset + 5) << 8) | byte(offset + 6)) === frame.width;
      }
      offset += length;
    }
  } catch { /* Invalid base64 / JPEG is not a preview. */ }
  return false;
}
